<template>
    <div>
        <header class="fixed w-full bg-white/95 backdrop-blur-md z-50 border-b border-gray-100 transition-all duration-300">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="flex justify-between items-center h-20">

                    <a href="/" class="flex-shrink-0 flex items-center gap-2 cursor-pointer">
                        <img class="h-8" src="https://storage.victoranw.fr/file/hGf4I3ecnS" alt="logo termoflow">
                        <h1 class="font-extrabold text-2xl tracking-tight text-gray-900">Termoflow</h1>
                    </a>

                    <nav class="hidden md:flex space-x-8 items-center">
                        <a href="/" class="text-gray-600 hover:text-orange-600 font-medium transition-colors">Accueil</a>

                        <!-- Fonctionnalités mega-menu -->
                        <div class="relative" @mouseleave="featuresOpen = false">
                            <button @click="featuresOpen = !featuresOpen" @mouseover="featuresOpen = true"
                                class="text-gray-600 hover:text-orange-600 font-medium inline-flex items-center transition-colors py-2">
                                Fonctionnalités
                                <i class="fa-solid fa-chevron-down ml-1 text-xs transition-transform duration-200"
                                    :class="{'rotate-180': featuresOpen}"></i>
                            </button>

                            <transition name="fade">
                                <div v-if="featuresOpen"
                                    class="absolute top-full left-1/2 transform -translate-x-1/2 mt-0 bg-white border border-gray-100 rounded-2xl shadow-xl z-50"
                                    style="width: 780px;">
                                    <div class="p-6">
                                        <!-- Groupes -->
                                        <div v-for="group in featureGroups" :key="group.label" class="mb-5 last:mb-0">
                                            <div class="flex items-center gap-2 mb-2.5">
                                                <i :class="group.icon" class="text-xs" :style="{color: group.color}"></i>
                                                <span class="text-xs font-black uppercase tracking-widest" :style="{color: group.color}">{{ group.label }}</span>
                                            </div>
                                            <div class="grid grid-cols-3 gap-1">
                                                <a :href="item.url" v-for="item in group.items" :key="item.name"
                                                    class="flex items-center gap-2.5 px-3 py-2 rounded-xl hover:bg-orange-50 group transition-colors">
                                                    <div class="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-all"
                                                        :style="{'background': group.iconBg, 'color': group.color}">
                                                        <i :class="item.icon" class="text-xs"></i>
                                                    </div>
                                                    <span class="text-sm font-semibold text-gray-700 group-hover:text-gray-900 leading-tight">{{ item.name }}</span>
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="border-t border-gray-100 px-6 py-3 flex items-center justify-between bg-gray-50 rounded-b-2xl">
                                        <span class="text-xs text-gray-400 font-medium">Un seul outil pour toute votre société CVC</span>
                                        <a href="/fonctionnalites" class="text-xs font-bold text-orange-600 hover:underline inline-flex items-center gap-1">
                                            Voir toutes les fonctionnalités <i class="fa-solid fa-arrow-right"></i>
                                        </a>
                                    </div>
                                </div>
                            </transition>
                        </div>

                        <!-- Ressources dropdown -->
                        <div class="relative" @mouseleave="resourcesOpen = false">
                            <button @click="resourcesOpen = !resourcesOpen" @mouseover="resourcesOpen = true"
                                class="text-gray-600 hover:text-orange-600 font-medium inline-flex items-center transition-colors py-2">
                                Ressources
                                <i class="fa-solid fa-chevron-down ml-1 text-xs transition-transform duration-200"
                                    :class="{'rotate-180': resourcesOpen}"></i>
                            </button>
                            <transition name="fade">
                                <div v-if="resourcesOpen"
                                    class="absolute top-full left-1/2 transform -translate-x-1/2 mt-0 w-64 bg-white border border-gray-100 rounded-2xl shadow-xl p-3 z-50">
                                    <a href="/roi/calculator" class="flex items-center gap-3 p-3 rounded-xl hover:bg-orange-50 group transition-colors">
                                        <div class="w-8 h-8 rounded-lg bg-gray-50 text-gray-400 flex items-center justify-center group-hover:bg-white group-hover:shadow-sm transition-all">
                                            <i class="fa-solid fa-calculator text-sm"></i>
                                        </div>
                                        <div>
                                            <span class="text-sm font-semibold text-gray-700 block">Calculateur ROI</span>
                                            <span class="text-xs text-gray-400">Estimez vos économies</span>
                                        </div>
                                    </a>
                                    <a href="/blog" class="flex items-center gap-3 p-3 rounded-xl hover:bg-orange-50 group transition-colors">
                                        <div class="w-8 h-8 rounded-lg bg-gray-50 text-gray-400 flex items-center justify-center group-hover:bg-white group-hover:shadow-sm transition-all">
                                            <i class="fa-solid fa-newspaper text-sm"></i>
                                        </div>
                                        <div>
                                            <span class="text-sm font-semibold text-gray-700 block">Blog</span>
                                            <span class="text-xs text-gray-400">Conseils & actualités CVC</span>
                                        </div>
                                    </a>
                                    <a href="/support" class="flex items-center gap-3 p-3 rounded-xl hover:bg-orange-50 group transition-colors">
                                        <div class="w-8 h-8 rounded-lg bg-gray-50 text-gray-400 flex items-center justify-center group-hover:bg-white group-hover:shadow-sm transition-all">
                                            <i class="fa-solid fa-life-ring text-sm"></i>
                                        </div>
                                        <div>
                                            <span class="text-sm font-semibold text-gray-700 block">Support</span>
                                            <span class="text-xs text-gray-400">On est là pour vous</span>
                                        </div>
                                    </a>
                                </div>
                            </transition>
                        </div>

                        <a href="/tarifs" class="text-gray-600 hover:text-orange-600 font-medium transition-colors">Tarifs</a>
                        <a href="/contact" class="text-gray-600 hover:text-orange-600 font-medium transition-colors">Contact</a>
                    </nav>

                    <div class="hidden md:flex items-center space-x-6">
                        <a href="/login" class="text-gray-900 font-semibold hover:text-orange-600 transition-colors">Connexion</a>
                        <a href="/register"
                            class="bg-gradient-to-r from-orange-500 to-red-600 text-white px-6 py-2.5 rounded-full font-bold hover:shadow-lg hover:shadow-orange-500/40 transition-all duration-300 transform hover:-translate-y-0.5">
                            Essai gratuit
                        </a>
                    </div>

                    <div class="md:hidden">
                        <button @click="mobileMenuOpen = !mobileMenuOpen" class="text-gray-600 p-2">
                            <i class="fa-solid fa-bars text-2xl"></i>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Mobile menu -->
            <transition name="slide-fade">
                <div v-if="mobileMenuOpen"
                    class="md:hidden bg-white border-b border-gray-100 p-4 shadow-xl absolute w-full max-h-[90vh] overflow-y-auto">
                    <button @click="mobileMenuOpen = false"
                        class="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-red-600 shadow-lg transition-all">
                        <i class="fas fa-times text-white text-lg"></i>
                    </button>
                    <div class="pt-10 space-y-1">
                        <a href="/" class="block text-gray-900 font-bold py-2 hover:text-orange-600">Accueil</a>
                    </div>

                    <div v-for="group in featureGroups" :key="group.label" class="mt-4">
                        <div class="flex items-center gap-1.5 px-2 mb-2">
                            <i :class="group.icon" class="text-xs" :style="{color: group.color}"></i>
                            <span class="text-xs font-black uppercase tracking-widest" :style="{color: group.color}">{{ group.label }}</span>
                        </div>
                        <div class="grid grid-cols-1 gap-0.5 pl-3 border-l-2 border-orange-100 ml-2">
                            <a :href="item.url" v-for="item in group.items" :key="item.name"
                                class="flex items-center gap-2 py-1.5 text-sm font-medium text-gray-600 hover:text-orange-600">
                                <i :class="item.icon" class="text-xs w-4 text-center opacity-60"></i>
                                {{ item.name }}
                            </a>
                        </div>
                    </div>

                    <div class="mt-4 pt-4 border-t border-gray-100">
                        <div class="font-bold text-gray-900 px-2 mb-2">Ressources</div>
                        <div class="grid grid-cols-1 gap-1 pl-2 border-l-2 border-orange-100 ml-2">
                            <a href="/roi/calculator" class="text-sm text-gray-600 py-1">Calculateur ROI</a>
                            <a href="/blog" class="text-sm text-gray-600 py-1">Blog</a>
                            <a href="/support" class="text-sm text-gray-600 py-1">Support</a>
                        </div>
                    </div>

                    <div class="mt-4 pt-4 border-t border-gray-100 space-y-2">
                        <a href="/tarifs" class="block text-gray-900 font-bold py-2 hover:text-orange-600">Tarifs</a>
                        <a href="/contact" class="block text-gray-900 font-bold py-2 hover:text-orange-600">Contact</a>
                    </div>
                    <div class="border-t border-gray-100 pt-4 mt-4 space-y-3">
                        <a href="/login" class="block text-center text-gray-900 font-semibold py-3 rounded-xl border-2 border-gray-200 hover:border-orange-500 hover:text-orange-600 transition-all">
                            Connexion
                        </a>
                        <button @click="$router.push({name: 'LandingContact'})"
                            class="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white font-bold py-3 rounded-xl shadow-md">
                            Demander une démo
                        </button>
                    </div>
                </div>
            </transition>
        </header>
    </div>
</template>

<script>
export default {
    data() {
        return {
            mobileMenuOpen: false,
            featuresOpen: false,
            resourcesOpen: false,
            featureGroups: [
                {
                    label: 'Terrain & chantiers',
                    icon: 'fa-solid fa-helmet-safety',
                    color: '#f97316',
                    iconBg: '#fff7ed',
                    items: [
                        { name: 'Interventions', icon: 'fa-solid fa-screwdriver-wrench', url: '/interventions' },
                        { name: 'Suivi des chantiers', icon: 'fa-solid fa-map-location-dot', url: '/chantier' },
                        { name: 'Application mobile', icon: 'fa-solid fa-mobile-screen-button', url: '/application-mobile' },
                        { name: 'Planning & tournées', icon: 'fa-solid fa-calendar-check', url: '/planification' },
                        { name: 'Prise de RDV en ligne', icon: 'fa-solid fa-calendar-plus', url: '/calendrier-reservation' },
                        { name: 'Gestion du stock', icon: 'fa-solid fa-boxes-stacked', url: '/stock' },
                    ]
                },
                {
                    label: 'Facturation & conformité',
                    icon: 'fa-solid fa-file-invoice-dollar',
                    color: '#16a34a',
                    iconBg: '#f0fdf4',
                    items: [
                        { name: 'Facturation', icon: 'fa-solid fa-file-invoice-dollar', url: '/facturation' },
                        { name: 'CERFA & fluides', icon: 'fa-solid fa-file-shield', url: '/cerfa' },
                        { name: 'Bouteilles & traçabilité', icon: 'fa-solid fa-bottle-droplet', url: '/bouteilles' },
                        { name: 'Détecteurs de gaz', icon: 'fa-solid fa-triangle-exclamation', url: '/detecteurs' },
                        { name: 'Exports & comptabilité', icon: 'fa-solid fa-file-export', url: '/exportation-donnees' },
                        { name: 'Intégrations', icon: 'fa-solid fa-plug', url: '/integration' },
                    ]
                },
                {
                    label: 'Clients & croissance',
                    icon: 'fa-solid fa-users',
                    color: '#7c3aed',
                    iconBg: '#f5f3ff',
                    items: [
                        { name: 'Gestion des clients', icon: 'fa-solid fa-user-tie', url: '/gestion-client' },
                        { name: 'Contrats d\'entretien', icon: 'fa-solid fa-file-contract', url: '/contrats-entretien' },
                        { name: 'Portail client', icon: 'fa-solid fa-globe', url: '/portail-client' },
                        { name: 'Communication auto', icon: 'fa-solid fa-paper-plane', url: '/communication-automatisee' },
                        { name: 'Statistiques & pilotage', icon: 'fa-solid fa-chart-line', url: '/statistiques' },
                        { name: 'Paramétrage métier', icon: 'fa-solid fa-sliders', url: '/parametrage' },
                    ]
                },
            ]
        }
    }
}
</script>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity 0.15s, transform 0.15s; }
.fade-enter-from, .fade-leave-to { opacity: 0; transform: translateY(-6px); }
.slide-fade-enter-active, .slide-fade-leave-active { transition: all 0.2s; }
.slide-fade-enter-from, .slide-fade-leave-to { opacity: 0; transform: translateY(-10px); }
</style>